/**
 * The review and planning cycles a quarterly meeting is built against.
 *
 * Four values, not two: a year alone does not identify a cycle and a quarter
 * alone is ambiguous across a year boundary, which is the common case — a Q4
 * meeting reviews Q4 of one year and plans Q1 of the next.
 *
 * Mirrored at `supabase/functions/_shared/quarterly-setup.ts`; this copy is
 * canonical — regenerate it with `bun run sync:shared`.
 */
import type { QuarterlyCycles } from '@/modules/meetings/meeting-instance';

// ─── shared body ────────────────────────────────────────────────────────────

export const QUARTERLY_SETUP_FIELDS = [
  'reviewYear', 'reviewQuarter', 'planningYear', 'planningQuarter',
] as const;

export type QuarterlySetupField = typeof QUARTERLY_SETUP_FIELDS[number];

/** A meeting type carries quarterly sections when any section key is namespaced to them. */
export function carriesQuarterlySections(sections: unknown): boolean {
  return Array.isArray(sections)
    && sections.some((section) => String((section as { key?: unknown })?.key ?? '').startsWith('quarterly-'));
}

/**
 * Which of the four are absent. Prep built against an unset cycle collects the
 * wrong rocks, so dispatch is skipped until all four are present.
 */
export function missingQuarterlyCycles(sectionData: unknown): QuarterlySetupField[] {
  const setup = (sectionData as Record<string, any> | null | undefined)?.['quarterly-setup'] ?? {};
  return QUARTERLY_SETUP_FIELDS.filter((field) => setup?.[field] == null);
}

export function quarterlyCyclesAreSet(sectionData: unknown): boolean {
  return missingQuarterlyCycles(sectionData).length === 0;
}

/**
 * The review and planning cycles, each resolved independently so a caller can
 * show what it has. Review is the latest cycle that has started; planning is
 * the first that exists after it. An `existing` setup wins outright — the
 * wizard's Setup step is where the pair is decided.
 */
export function resolveQuarterlyCyclePair(
  availableQuarters: { year: number; quarter: number | null }[],
  currentYear: number,
  currentQuarter: number,
  existing?: Record<string, any> | null,
): { review: { year: number; quarter: number } | null; planning: { year: number; quarter: number } | null } {
  const setup = existing?.['quarterly-setup'];
  const qVal = (year: number, quarter: number) => year * 4 + quarter;
  const dated = availableQuarters
    .filter((q): q is { year: number; quarter: number } => q.quarter != null);

  const review = setup?.reviewYear != null && setup?.reviewQuarter != null
    ? { year: setup.reviewYear, quarter: setup.reviewQuarter }
    : [...dated]
        .filter((q) => qVal(q.year, q.quarter) <= qVal(currentYear, currentQuarter))
        .sort((a, b) => qVal(b.year, b.quarter) - qVal(a.year, a.quarter))[0] ?? null;

  const planning = setup?.planningYear != null && setup?.planningQuarter != null
    ? { year: setup.planningYear, quarter: setup.planningQuarter }
    // The cycle after the one being reviewed, but only if one exists for it —
    // the wizard's Setup step says to create one in Planning otherwise.
    : [...dated]
        .filter((q) => qVal(q.year, q.quarter) > (review ? qVal(review.year, review.quarter) : 0))
        .sort((a, b) => qVal(a.year, a.quarter) - qVal(b.year, b.quarter))[0] ?? null;

  return { review, planning };
}

/**
 * The same pair as the four stored fields, or `null` unless both resolve.
 *
 * All four or nothing: `missingQuarterlyCycles` reads a partial set as absent,
 * so writing one would disguise the gap rather than close it.
 */
export function resolveQuarterlyCycles(
  availableQuarters: { year: number; quarter: number | null }[],
  currentYear: number,
  currentQuarter: number,
  existing?: Record<string, any> | null,
): QuarterlyCycles | null {
  const { review, planning } = resolveQuarterlyCyclePair(availableQuarters, currentYear, currentQuarter, existing);
  if (!review || !planning) return null;
  return {
    reviewYear: review.year,
    reviewQuarter: review.quarter,
    planningYear: planning.year,
    planningQuarter: planning.quarter,
  };
}
